import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Button, Collapse, Divider, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { Modal } from '@app/ui/modal';
import { Alert } from '@app/ui/alert';
import { Label } from '@app/ui/label';
import { Text } from '@app/ui/forms';
import { Tooltip } from '@app/ui/tooltip';
import { error as toastError } from '@app/snackbars';
import { ScoreJustificationModal } from '@app/matching/components/ScoreJustificationModal';
import { buildJustificationView } from '@app/matching/utils/build-justification-view';

import { RULES_DRY_RUN_GET_ONE, RULES_DRY_RUN_START } from '../gql';
import {
  ColorDryRunCaseResult,
  DryRunCaseInput,
  DryRunCaseResult,
  MatchingDryRunCaseResult,
  MatchingDryRunCaseStatus,
  MatchingModelIdSource,
  MatchingPairMode,
  RuleDryRun,
  RuleFamily,
  RuleSetVersion,
} from '../types';
import { DRY_RUN_SAMPLE_PROFILES } from '../data/dryRunSampleProfiles';
import { DRY_RUN_COLOR_SAMPLE_PROFILES } from '../data/dryRunColorSampleProfiles';
import { DRY_RUN_MATCHING_SAMPLE_PAIRS } from '../data/dryRunMatchingSamplePairs';
import { SEVERITY_LABEL_COLOR } from '../constants';
import { getGraphQLErrorMessage, matchingGradeLabelKey, parseJsonObject, unevaluableSkips } from '../utils';

export interface DryRunBenchProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly family: RuleFamily;
  readonly ruleSetVersionId: number;
  readonly versions: RuleSetVersion[];
}

type CaseSource = 'synthetic' | 'realProfile';

const POLL_INTERVAL_MS = 2000;
// Watchlist/colour behaviour is UNCHANGED — a bounded run gives up after this (their SPEC bounds
// them to ~20s). Matching runs on its own mechanism below (no fixed cutoff): a live 6-case run took
// 173s in the engine and completed fine server-side, but this fixed window gave up on it client-side
// before the result was ever shown.
const POLL_MAX_ATTEMPTS = 40;
const POLL_DEADLINE_MS = POLL_INTERVAL_MS * POLL_MAX_ATTEMPTS;
const MATCHING_FAN_OUT_MAX = 20;
// Matching-only: a soft, dismissible notice past this point — never an automatic stop.
const MATCHING_STILL_RUNNING_THRESHOLD_MS = 5 * 60 * 1000;
// Mirrors the backend's `MATCHING_BENCH_STALE_MS` (`rules-dry-run.service.ts`): how long a matching
// run survives server-side, so the "resume it later" promise below names a true limit.
const MATCHING_RUN_KEPT_MINUTES = 30;
// Matching-only: roughly what one pair costs (SPEC §3's per-pair model call), used for the on-screen
// expectation line only — never a cutoff.
const MATCHING_SECONDS_PER_CASE = 30;

// `"12, 34,56"` -> `[12, 34, 56]`, deduped, capped at the shared dry-run case cap — mirrors the
// backend's own `@ArrayMaxSize(DRY_RUN_MAX_CASES)` so an over-long paste fails the SAME way the
// server would, just earlier.
const parseIdList = (raw: string): number[] =>
  Array.from(
    new Set(
      raw
        .split(/[,\s]+/)
        .map(part => Number(part.trim()))
        .filter(value => Number.isInteger(value) && value > 0),
    ),
  ).slice(0, MATCHING_FAN_OUT_MAX);

// Matching-only resumability: the dry-run row lives server-side until the terminal read consumes it
// (`getOneAndMaybeDelete`), so closing the bench or reloading the page mid-run does not lose the
// run — only the client's own poll. `sessionStorage` per the house pattern (`useWatchlistViewed.ts`):
// wrapped in try/catch, never assumed available (private mode / quota).
interface MatchingInFlightRun {
  dryRunId: string;
  startedAt: number;
  caseCount: number;
}

const matchingInFlightStorageKey = (family: RuleFamily) => `rules-dry-run-inflight:${family}`;

const readMatchingInFlightRun = (family: RuleFamily): MatchingInFlightRun | null => {
  try {
    const raw = sessionStorage.getItem(matchingInFlightStorageKey(family));

    return raw ? (JSON.parse(raw) as MatchingInFlightRun) : null;
  } catch {
    return null;
  }
};

const writeMatchingInFlightRun = (family: RuleFamily, run: MatchingInFlightRun): void => {
  try {
    sessionStorage.setItem(matchingInFlightStorageKey(family), JSON.stringify(run));
  } catch {
    // storage unavailable — the run still completes server-side, only the resume offer is lost
  }
};

const clearMatchingInFlightRun = (family: RuleFamily): void => {
  try {
    sessionStorage.removeItem(matchingInFlightStorageKey(family));
  } catch {
    // nothing to clean up if storage was never reachable
  }
};

// "45s" / "3m 12s" — the running elapsed-time display.
const formatElapsedSeconds = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

// Matching-only metrics display: "23200" -> "23.2k", "480" -> "480".
const formatTokenCount = (tokens: number | null | undefined): string => {
  if (tokens == null) {
    return '–';
  }

  return tokens >= 1000 ? `${(tokens / 1000).toFixed(1)}k` : String(tokens);
};

const formatCostUsd = (cost: number | null | undefined): string => (cost == null ? '–' : cost.toFixed(2));

// "1400" (ms) -> "1.4" (s, one decimal — call durations are usually sub-10s).
const formatSecondsFromMs = (ms: number | null | undefined): string => (ms == null ? '–' : (ms / 1000).toFixed(1));

export const DryRunBench = ({ open, onClose, family, ruleSetVersionId, versions }: DryRunBenchProps) => {
  const { t } = useTranslation('common');
  const isColorCoding = family === RuleFamily.colorCoding;
  const isMatching = family === RuleFamily.matching;
  const sampleProfiles = isColorCoding ? DRY_RUN_COLOR_SAMPLE_PROFILES : DRY_RUN_SAMPLE_PROFILES;

  const [selectedVersionId, setSelectedVersionId] = useState(ruleSetVersionId);
  const [caseSource, setCaseSource] = useState<CaseSource>('synthetic');
  const [sampleId, setSampleId] = useState(isMatching ? DRY_RUN_MATCHING_SAMPLE_PAIRS[0].id : sampleProfiles[0].id);
  const [customProfileJson, setCustomProfileJson] = useState('{}');
  // G4/M6a — the watchlist source is an `applicationId`, the colour source a `questionnaireId`; the
  // input field is the one thing that differs between the two "real profile" sources.
  const [realProfileIdInput, setRealProfileIdInput] = useState('');
  // Matching's real-row source is a PAIR, not a single id (SPEC §7.5) — three shapes, one anchor id
  // each plus an optional fan-out id list on the other side.
  const [matchingPairMode, setMatchingPairMode] = useState<MatchingPairMode>(MatchingPairMode.pair);
  const [matchingGcIdInput, setMatchingGcIdInput] = useState('');
  const [matchingIpIdInput, setMatchingIpIdInput] = useState('');
  const [matchingGcIdsInput, setMatchingGcIdsInput] = useState('');
  const [matchingIpIdsInput, setMatchingIpIdsInput] = useState('');
  const [realProfileConfirmed, setRealProfileConfirmed] = useState(false);
  // A different id is a different person, so the confirmation must be given again.
  const changeRealProfileId = (setId: (value: string) => void, value: string) => {
    setId(value);
    setRealProfileConfirmed(false);
  };
  const [dryRunId, setDryRunId] = useState<string | null>(null);
  const [result, setResult] = useState<RuleDryRun | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [expandedCaseIds, setExpandedCaseIds] = useState<string[]>([]);
  // Matching-only: when the run started (epoch ms — set on a fresh start AND on resume, so elapsed
  // reads correctly either way) and how many cases it covers, for the expectation line.
  const [matchingRunStartedAt, setMatchingRunStartedAt] = useState<number | null>(null);
  const [matchingRunCaseCount, setMatchingRunCaseCount] = useState<number | null>(null);
  const [matchingStillRunningDismissed, setMatchingStillRunningDismissed] = useState(false);
  const [matchingResumableRun, setMatchingResumableRun] = useState<MatchingInFlightRun | null>(null);
  // Ticks the elapsed-time display once a second while a matching run is in flight.
  const [now, setNow] = useState(() => Date.now());
  // Which matching case's score breakdown is open — a single id, since only one modal renders at a time.
  const [justificationCaseId, setJustificationCaseId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedVersionId(ruleSetVersionId);
      setDryRunId(null);
      setResult(null);
      setTimedOut(false);
      setRealProfileConfirmed(false);
      setExpandedCaseIds([]);
      setMatchingPairMode(MatchingPairMode.pair);
      setMatchingGcIdInput('');
      setMatchingIpIdInput('');
      setMatchingGcIdsInput('');
      setMatchingIpIdsInput('');
      setMatchingRunStartedAt(null);
      setMatchingRunCaseCount(null);
      setMatchingStillRunningDismissed(false);
      setMatchingResumableRun(isMatching ? readMatchingInFlightRun(family) : null);
      setJustificationCaseId(null);
    }
  }, [open, ruleSetVersionId, isMatching, family]);

  const [startDryRun, { loading: starting }] = useMutation<{ rulesDryRunStart: RuleDryRun }>(RULES_DRY_RUN_START);

  const { data: pollData, stopPolling } = useQuery<{ rulesDryRunGetOne: RuleDryRun | null }>(RULES_DRY_RUN_GET_ONE, {
    variables: { dryRunId },
    skip: !dryRunId || !!result || timedOut,
    pollInterval: POLL_INTERVAL_MS,
    fetchPolicy: 'network-only',
  });

  // Watchlist/colour ONLY — a wall-clock timer, not a poll counter (a repeated `null` response leaves
  // Apollo's `data` reference unchanged, so there is nothing per-attempt to count). Matching never
  // sets `timedOut`: it has its own no-cutoff mechanism below.
  useEffect(() => {
    if (isMatching || !dryRunId || result) {
      return undefined;
    }

    const timer = setTimeout(() => setTimedOut(true), POLL_DEADLINE_MS);

    return () => clearTimeout(timer);
  }, [isMatching, dryRunId, result]);

  // Matching ONLY — ticks the elapsed-time display; polling itself keeps running via `pollInterval`
  // above regardless of how long this has been running (no cutoff).
  useEffect(() => {
    if (!isMatching || !dryRunId || result) {
      return undefined;
    }

    const interval = setInterval(() => setNow(Date.now()), 1000);

    return () => clearInterval(interval);
  }, [isMatching, dryRunId, result]);

  // The row is DELETE-ON-READ once terminal, so capture the first terminal response and stop. A
  // change of `dryRunId` tears this poll down by itself — Apollo re-keys polling by variables.
  useEffect(() => {
    if (!pollData || !('rulesDryRunGetOne' in pollData)) {
      return;
    }

    const row = pollData.rulesDryRunGetOne;

    if (row && (row.status === 'completed' || row.status === 'failed')) {
      setResult(row);
      stopPolling();

      if (isMatching) {
        clearMatchingInFlightRun(family);
      }

      return;
    }

    // Matching only, and only once a poll is actually in flight: a `null` row means the id refers to
    // a run that is GONE — already delivered in an earlier session, or (rarely) swept as stale. A
    // fresh start never sees this (the row exists the instant `rulesDryRunStart` returns), so it can
    // only happen on a resumed id.
    if (isMatching && dryRunId && row === null) {
      clearMatchingInFlightRun(family);
      setDryRunId(null);
      toastError(t('rules.dryRun.resumeNotFound'));
    }
  }, [pollData, stopPolling, isMatching, family, dryRunId, t]);

  const familyVersions = useMemo(() => versions.filter(version => version.family === family), [versions, family]);

  const handleRun = async () => {
    let cases: DryRunCaseInput[] | undefined;
    let applicationId: number | undefined;
    let questionnaireId: number | undefined;
    let gcApplicationId: number | undefined;
    let ipQuestionnaireId: number | undefined;
    let gcApplicationIds: number[] | undefined;
    let ipQuestionnaireIds: number[] | undefined;
    // Matching only — persisted alongside `dryRunId` for the resumed session's expectation line.
    let matchingCaseCount = 1;

    if (caseSource === 'synthetic') {
      if (isMatching) {
        const sample = DRY_RUN_MATCHING_SAMPLE_PAIRS.find(candidate => candidate.id === sampleId);

        if (!sample) {
          toastError(t('rules.dryRun.invalidProfileJson'));

          return;
        }

        cases = [{ caseId: 'bench-1', label: t(sample.labelKey) ?? 'bench-1', profile: sample.profile }];
      } else {
        const sample = sampleProfiles.find(candidate => candidate.id === sampleId);
        const profile = sample ? sample.profile : parseJsonObject(customProfileJson);

        if (!profile) {
          toastError(t('rules.dryRun.invalidProfileJson'));

          return;
        }

        const label = (sample ? t(sample.labelKey) : t('rules.dryRun.customCaseLabel')) ?? 'bench-1';

        cases = [{ caseId: 'bench-1', label, profile }];
      }
    } else if (isMatching) {
      const gcId = Number(matchingGcIdInput);
      const ipId = Number(matchingIpIdInput);

      if (matchingPairMode === MatchingPairMode.pair) {
        if (!matchingGcIdInput || !matchingIpIdInput || Number.isNaN(gcId) || Number.isNaN(ipId)) {
          toastError(t('rules.matching.dryRun.invalidPairIds'));

          return;
        }

        gcApplicationId = gcId;
        ipQuestionnaireId = ipId;
      } else if (matchingPairMode === MatchingPairMode.gcFanOut) {
        const ipIds = parseIdList(matchingIpIdsInput);

        if (!matchingGcIdInput || Number.isNaN(gcId) || !ipIds.length) {
          toastError(t('rules.matching.dryRun.invalidFanOutIds'));

          return;
        }

        gcApplicationId = gcId;
        ipQuestionnaireIds = ipIds;
        matchingCaseCount = ipIds.length;
      } else if (matchingPairMode === MatchingPairMode.ipFanOut) {
        const gcIds = parseIdList(matchingGcIdsInput);

        if (!matchingIpIdInput || Number.isNaN(ipId) || !gcIds.length) {
          toastError(t('rules.matching.dryRun.invalidFanOutIds'));

          return;
        }

        ipQuestionnaireId = ipId;
        gcApplicationIds = gcIds;
        matchingCaseCount = gcIds.length;
      } else {
        const gcIds = parseIdList(matchingGcIdsInput);
        const ipIds = parseIdList(matchingIpIdsInput);

        if (!gcIds.length || !ipIds.length || gcIds.length * ipIds.length > MATCHING_FAN_OUT_MAX) {
          toastError(t('rules.matching.dryRun.invalidBothFanOutIds'));

          return;
        }

        gcApplicationIds = gcIds;
        ipQuestionnaireIds = ipIds;
        matchingCaseCount = gcIds.length * ipIds.length;
      }
    } else {
      const parsedId = Number(realProfileIdInput);

      if (!realProfileIdInput || Number.isNaN(parsedId)) {
        toastError(isColorCoding ? t('rules.dryRun.invalidQuestionnaireId') : t('rules.dryRun.invalidApplicationId'));

        return;
      }

      if (isColorCoding) {
        questionnaireId = parsedId;
      } else {
        applicationId = parsedId;
      }
    }

    try {
      const { data } = await startDryRun({
        variables: {
          input: {
            family,
            ruleSetVersionId: selectedVersionId,
            cases,
            applicationId,
            questionnaireId,
            gcApplicationId,
            ipQuestionnaireId,
            gcApplicationIds,
            ipQuestionnaireIds,
          },
        },
      });

      if (data?.rulesDryRunStart?.dryRunId) {
        const startedDryRunId = data.rulesDryRunStart.dryRunId;

        setResult(null);
        setTimedOut(false);
        setDryRunId(startedDryRunId);

        if (isMatching) {
          const startedAt = Date.now();

          setMatchingRunStartedAt(startedAt);
          setMatchingRunCaseCount(matchingCaseCount);
          setMatchingStillRunningDismissed(false);
          setMatchingResumableRun(null);
          setNow(startedAt);
          writeMatchingInFlightRun(family, { dryRunId: startedDryRunId, startedAt, caseCount: matchingCaseCount });
        }
      }
    } catch (e) {
      // Surface the server's actual reason (e.g. an in-flight run's elapsed time), not a generic
      // toast that hides it.
      const reason = getGraphQLErrorMessage(e);

      toastError(reason ? t('rules.dryRun.startFailed', { reason }) : t('general.error.somethingWentWrong'));
    }
  };

  // Back to the case picker with inputs intact; an immediate re-run just hits the in-flight guard.
  const handleRetry = () => {
    setDryRunId(null);
    setResult(null);
    setTimedOut(false);
  };

  // Re-attaches to a run started in an earlier session (this dialog closed mid-run, or the page
  // reloaded) — the row is still server-side (delete-on-read only fires on a TERMINAL read), so the
  // very next poll just picks up where a fresh run's first poll would have.
  const handleResumeRun = () => {
    if (!matchingResumableRun) {
      return;
    }

    setResult(null);
    setTimedOut(false);
    setMatchingRunStartedAt(matchingResumableRun.startedAt);
    setMatchingRunCaseCount(matchingResumableRun.caseCount);
    setMatchingStillRunningDismissed(false);
    setNow(Date.now());
    setDryRunId(matchingResumableRun.dryRunId);
    setMatchingResumableRun(null);
  };

  const toggleCase = (caseId: string) =>
    setExpandedCaseIds(current =>
      current.includes(caseId) ? current.filter(id => id !== caseId) : [...current, caseId],
    );

  const running = !!dryRunId && !result && !timedOut;
  const matchingElapsedSeconds = matchingRunStartedAt
    ? Math.max(0, Math.floor((now - matchingRunStartedAt) / 1000))
    : 0;
  const matchingStillRunningNoticeVisible =
    isMatching &&
    running &&
    !matchingStillRunningDismissed &&
    matchingElapsedSeconds * 1000 >= MATCHING_STILL_RUNNING_THRESHOLD_MS;
  const matchingExpectedMinutes = matchingRunCaseCount
    ? Math.max(1, Math.ceil((matchingRunCaseCount * MATCHING_SECONDS_PER_CASE) / 60))
    : 1;

  // Live product count for the "several GCs × several IPs" shape — parsed/deduped the SAME way
  // `handleRun` parses them, so what the coordinator reads before clicking Run matches what a click
  // would actually send.
  const matchingBothFanOutGcIds = parseIdList(matchingGcIdsInput);
  const matchingBothFanOutIpIds = parseIdList(matchingIpIdsInput);
  const matchingBothFanOutCount = matchingBothFanOutGcIds.length * matchingBothFanOutIpIds.length;
  const matchingBothFanOutInvalid =
    matchingPairMode === MatchingPairMode.bothFanOut &&
    (!matchingBothFanOutGcIds.length ||
      !matchingBothFanOutIpIds.length ||
      matchingBothFanOutCount > MATCHING_FAN_OUT_MAX);

  // `payload.cases[]` holds `DryRunCaseResult[]` for a watchlist run, `ColorDryRunCaseResult[]` for
  // a colour one, `MatchingDryRunCaseResult[]` for a matching one — the family is already known
  // here, so casting at this one branch point is safe.
  const watchlistCases =
    (!isColorCoding && !isMatching ? (result?.payload?.cases as DryRunCaseResult[] | undefined) : undefined) ?? [];
  const colorCases =
    (isColorCoding ? (result?.payload?.cases as ColorDryRunCaseResult[] | undefined) : undefined) ?? [];
  const matchingCases =
    (isMatching ? (result?.payload?.cases as MatchingDryRunCaseResult[] | undefined) : undefined) ?? [];
  // ⚠ No typed GraphQL path — `RuleDryRunDto.payload` is `GraphQLJSON`, read the same way colour's
  // per-case `tokensIn`/`tokensOut` always were (backend gap, see `MatchingDryRunMetrics`'s comment).
  const matchingMetrics = isMatching ? result?.payload?.metrics ?? null : null;

  // Each stat is its own segment, gated independently on presence — a partial rollout (an older
  // producer, a field the engine didn't compute this run) drops just that clause, never a sentence
  // full of dashes.
  const matchingMetricsSegments: string[] = [];

  if (matchingMetrics) {
    matchingMetricsSegments.push(t('rules.matching.dryRun.metrics.caseCountLabel', { count: matchingCases.length }));

    if (matchingMetrics.modelCalls) {
      matchingMetricsSegments.push(
        t('rules.matching.dryRun.metrics.modelCallsLabel', {
          count: matchingMetrics.modelCalls.digest + matchingMetrics.modelCalls.grade,
        }),
      );
    }

    if (matchingMetrics.concurrency != null) {
      matchingMetricsSegments.push(
        t('rules.matching.dryRun.metrics.concurrencyLabel', { concurrency: matchingMetrics.concurrency }),
      );
    }

    if (matchingMetrics.cacheHits != null) {
      matchingMetricsSegments.push(
        t('rules.matching.dryRun.metrics.cacheHitsLabel', { count: matchingMetrics.cacheHits }),
      );
    }

    if (matchingMetrics.tokensIn != null || matchingMetrics.tokensOut != null) {
      matchingMetricsSegments.push(
        t('rules.matching.dryRun.metrics.tokensLabel', {
          tokensIn: formatTokenCount(matchingMetrics.tokensIn),
          tokensOut: formatTokenCount(matchingMetrics.tokensOut),
        }),
      );
    }

    if (matchingMetrics.callDurationMs) {
      matchingMetricsSegments.push(
        t('rules.matching.dryRun.metrics.callDurationLabel', {
          meanSeconds: formatSecondsFromMs(matchingMetrics.callDurationMs.mean),
          maxSeconds: formatSecondsFromMs(matchingMetrics.callDurationMs.max),
        }),
      );
    }

    if (matchingMetrics.sumCallDurationsMs != null && matchingMetrics.wallMs != null) {
      matchingMetricsSegments.push(
        t('rules.matching.dryRun.metrics.parallelismLabel', {
          sumSeconds: Math.round(matchingMetrics.sumCallDurationsMs / 1000),
          wallSeconds: Math.round(matchingMetrics.wallMs / 1000),
          ratio: matchingMetrics.parallelismRatio != null ? matchingMetrics.parallelismRatio.toFixed(1) : '–',
        }),
      );
    } else if (matchingMetrics.wallMs != null) {
      matchingMetricsSegments.push(
        t('rules.matching.dryRun.metrics.wallLabel', { wallSeconds: Math.round(matchingMetrics.wallMs / 1000) }),
      );
    }

    if (matchingMetrics.estimatedCostUsd != null) {
      matchingMetricsSegments.push(
        t('rules.matching.dryRun.metrics.costLabel', { cost: formatCostUsd(matchingMetrics.estimatedCostUsd) }),
      );
    }
  }

  // `skipped[]` arrives on the TYPED `cases` field for ALL THREE families — `RuleDryRunFieldResolver`
  // normalises it by `family`, so this one map serves every render branch alike.
  const unevaluableByCaseId = new Map(
    (result?.cases ?? []).map(entry => [entry.caseId, unevaluableSkips(entry.skipped)]),
  );
  // Matching shows EVERY skip, not just the "could not evaluate" subset — N/A is the family's own
  // central concept (SPEC §3.3 "missing data should never quietly punish"), not a filtered edge case.
  const matchingSkipsByCaseId = new Map((result?.cases ?? []).map(entry => [entry.caseId, entry.skipped]));

  const justificationCase = justificationCaseId
    ? matchingCases.find(caseResult => caseResult.caseId === justificationCaseId) ?? null
    : null;
  const justificationView = buildJustificationView(
    justificationCase
      ? {
          score: justificationCase.score,
          weightApplied: justificationCase.weightApplied,
          summary: justificationCase.summary,
          pros: justificationCase.pros,
          cons: justificationCase.cons,
          hardFails: justificationCase.hardFails,
          breakdown: justificationCase.breakdown,
          skipped: matchingSkipsByCaseId.get(justificationCase.caseId) ?? [],
          label: justificationCase.label,
        }
      : null,
  );

  return (
    <Modal open={open} onClose={onClose} disableBackdropClose maxWidth="md" title={t('rules.dryRun.title')}>
      <Alert severity="warning" sx={{ mb: 2 }}>
        {t('rules.dryRun.simulationBanner')}
      </Alert>

      {familyVersions.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <Text
            select
            label={t('rules.dryRun.versionPicker')}
            helperText={t('rules.dryRun.versionPickerHelperText')}
            value={selectedVersionId}
            readOnly={!!dryRunId || !!result || timedOut}
            onChange={e => setSelectedVersionId(Number(e.target.value))}
            options={familyVersions.map(version => ({
              id: version.id,
              name:
                version.status === 'draft'
                  ? `${t('rules.versionHistory.statusDraft')} v${version.version}`
                  : `v${version.version}`,
            }))}
          />
        </Box>
      )}

      {!running && !result && !timedOut && (
        <>
          {isMatching && matchingResumableRun && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {t('rules.dryRun.resumeAvailableNotice')}
                </Typography>
                <Button size="small" variant="contained" color="primaryLight" onClick={handleResumeRun}>
                  {t('rules.dryRun.resumeButton')}
                </Button>
              </Box>
            </Alert>
          )}

          <Text
            select
            label={t('rules.dryRun.caseSourceLabel')}
            helperText={t('rules.dryRun.caseSourceHelperText')}
            value={caseSource}
            onChange={e => {
              setCaseSource(e.target.value as CaseSource);
              setRealProfileConfirmed(false);
            }}
            options={[
              { id: 'synthetic', name: t('rules.dryRun.caseSource.synthetic') ?? '' },
              {
                id: 'realProfile',
                name:
                  (isMatching
                    ? t('rules.matching.dryRun.caseSource.realPair')
                    : isColorCoding
                    ? t('rules.dryRun.caseSource.realQuestionnaire')
                    : t('rules.dryRun.caseSource.realProfile')) ?? '',
              },
            ]}
            sx={{ mb: 2 }}
          />

          {caseSource === 'synthetic' ? (
            isMatching ? (
              <Text
                select
                label={t('rules.dryRun.syntheticCasePicker')}
                helperText={t('rules.dryRun.syntheticCasePickerHelperText')}
                value={sampleId}
                onChange={e => setSampleId(e.target.value)}
                options={DRY_RUN_MATCHING_SAMPLE_PAIRS.map(sample => ({
                  id: sample.id,
                  name: t(sample.labelKey) ?? '',
                }))}
                sx={{ mb: 2 }}
              />
            ) : (
              <>
                <Text
                  select
                  label={t('rules.dryRun.syntheticCasePicker')}
                  helperText={t('rules.dryRun.syntheticCasePickerHelperText')}
                  value={sampleId}
                  onChange={e => setSampleId(e.target.value)}
                  options={[
                    ...sampleProfiles.map(sample => ({ id: sample.id, name: t(sample.labelKey) ?? '' })),
                    { id: 'custom', name: t('rules.dryRun.customCaseLabel') ?? '' },
                  ]}
                  sx={{ mb: 2 }}
                />

                {sampleId === 'custom' && (
                  <Text
                    multiline
                    rows={6}
                    label={t('rules.dryRun.customProfileJsonLabel')}
                    helperText={t('rules.dryRun.customProfileJsonHelperText')}
                    placeholder={t('rules.dryRun.customProfileJsonPlaceholder') ?? undefined}
                    value={customProfileJson}
                    onChange={e => setCustomProfileJson(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                )}
              </>
            )
          ) : isMatching ? (
            <>
              <Text
                select
                label={t('rules.matching.dryRun.pairModeLabel')}
                helperText={t('rules.matching.dryRun.pairModeHelperText')}
                value={matchingPairMode}
                onChange={e => {
                  setMatchingPairMode(e.target.value as MatchingPairMode);
                  setRealProfileConfirmed(false);
                }}
                options={[
                  { id: MatchingPairMode.pair, name: t('rules.matching.dryRun.pairMode.pair') ?? '' },
                  { id: MatchingPairMode.gcFanOut, name: t('rules.matching.dryRun.pairMode.gcFanOut') ?? '' },
                  { id: MatchingPairMode.ipFanOut, name: t('rules.matching.dryRun.pairMode.ipFanOut') ?? '' },
                  { id: MatchingPairMode.bothFanOut, name: t('rules.matching.dryRun.pairMode.bothFanOut') ?? '' },
                ]}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                {(matchingPairMode === MatchingPairMode.pair || matchingPairMode === MatchingPairMode.gcFanOut) && (
                  <Box sx={{ flexGrow: 1, minWidth: 160 }}>
                    <Text
                      label={t('rules.matching.dryRun.gcApplicationIdLabel')}
                      placeholder={t('rules.dryRun.realProfileSearchPlaceholder') ?? undefined}
                      value={matchingGcIdInput}
                      onChange={e => changeRealProfileId(setMatchingGcIdInput, e.target.value)}
                    />
                  </Box>
                )}

                {(matchingPairMode === MatchingPairMode.pair || matchingPairMode === MatchingPairMode.ipFanOut) && (
                  <Box sx={{ flexGrow: 1, minWidth: 160 }}>
                    <Text
                      label={t('rules.matching.dryRun.ipQuestionnaireIdLabel')}
                      placeholder={t('rules.dryRun.realProfileSearchPlaceholder') ?? undefined}
                      value={matchingIpIdInput}
                      onChange={e => changeRealProfileId(setMatchingIpIdInput, e.target.value)}
                    />
                  </Box>
                )}

                {(matchingPairMode === MatchingPairMode.ipFanOut ||
                  matchingPairMode === MatchingPairMode.bothFanOut) && (
                  <Box sx={{ flexGrow: 1, minWidth: 240 }}>
                    <Text
                      label={t('rules.matching.dryRun.gcApplicationIdsLabel')}
                      helperText={t('rules.matching.dryRun.idsHelperText')}
                      value={matchingGcIdsInput}
                      onChange={e => changeRealProfileId(setMatchingGcIdsInput, e.target.value)}
                    />
                  </Box>
                )}

                {(matchingPairMode === MatchingPairMode.gcFanOut ||
                  matchingPairMode === MatchingPairMode.bothFanOut) && (
                  <Box sx={{ flexGrow: 1, minWidth: 240 }}>
                    <Text
                      label={t('rules.matching.dryRun.ipQuestionnaireIdsLabel')}
                      helperText={t('rules.matching.dryRun.idsHelperText')}
                      value={matchingIpIdsInput}
                      onChange={e => changeRealProfileId(setMatchingIpIdsInput, e.target.value)}
                    />
                  </Box>
                )}
              </Box>

              {matchingPairMode === MatchingPairMode.bothFanOut && (
                <Alert severity={matchingBothFanOutInvalid ? 'error' : 'info'} sx={{ mb: 2 }}>
                  {t('rules.matching.dryRun.productCountLabel', {
                    gcCount: matchingBothFanOutGcIds.length,
                    ipCount: matchingBothFanOutIpIds.length,
                    total: matchingBothFanOutCount,
                    max: MATCHING_FAN_OUT_MAX,
                  })}
                </Alert>
              )}

              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('rules.dryRun.realProfileConfirm')}
              </Alert>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button variant="contained" color="primaryLight" onClick={() => setRealProfileConfirmed(true)}>
                  {t('rules.dryRun.realProfileConfirmButton')}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Text
                label={isColorCoding ? t('rules.dryRun.realQuestionnaireSearch') : t('rules.dryRun.realProfileSearch')}
                helperText={
                  isColorCoding
                    ? t('rules.dryRun.realQuestionnaireSearchHelperText')
                    : t('rules.dryRun.realProfileSearchHelperText')
                }
                placeholder={t('rules.dryRun.realProfileSearchPlaceholder') ?? undefined}
                value={realProfileIdInput}
                onChange={e => changeRealProfileId(setRealProfileIdInput, e.target.value)}
                sx={{ mb: 2 }}
              />

              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('rules.dryRun.realProfileConfirm')}
              </Alert>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button variant="contained" color="primaryLight" onClick={() => setRealProfileConfirmed(true)}>
                  {t('rules.dryRun.realProfileConfirmButton')}
                </Button>
              </Box>
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip
              title={
                matchingBothFanOutInvalid
                  ? t('rules.matching.dryRun.productCountExceeded') ?? undefined
                  : caseSource === 'realProfile' && !realProfileConfirmed
                  ? t('rules.dryRun.runDisabledTooltip') ?? undefined
                  : undefined
              }
            >
              <span>
                <LoadingButton
                  loading={starting}
                  variant="contained"
                  color="primary"
                  disabled={(caseSource === 'realProfile' && !realProfileConfirmed) || matchingBothFanOutInvalid}
                  onClick={handleRun}
                >
                  {t('rules.dryRun.runButton')}
                </LoadingButton>
              </span>
            </Tooltip>
          </Box>
        </>
      )}

      {running && isMatching && (
        <Box>
          <Typography variant="body1" color="text.secondary">
            {t('rules.dryRun.runningElapsedLabel', { elapsed: formatElapsedSeconds(matchingElapsedSeconds) })}
          </Typography>

          {!!matchingRunCaseCount && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('rules.dryRun.matchingExpectationLabel', {
                count: matchingRunCaseCount,
                minutes: matchingExpectedMinutes,
              })}
            </Typography>
          )}

          {matchingStillRunningNoticeVisible && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('rules.dryRun.stillRunningNotice', { minutes: MATCHING_RUN_KEPT_MINUTES })}
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button size="small" onClick={onClose}>
                  {t('rules.dryRun.closeButton')}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primaryLight"
                  onClick={() => setMatchingStillRunningDismissed(true)}
                >
                  {t('rules.dryRun.keepWaitingButton')}
                </Button>
              </Box>
            </Alert>
          )}
        </Box>
      )}

      {running && !isMatching && (
        <Typography variant="body1" color="text.secondary">
          {t('rules.dryRun.runningNotice')}
        </Typography>
      )}

      {timedOut && !result && (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('rules.dryRun.timedOutNotice', { seconds: POLL_DEADLINE_MS / 1000 })}
          </Alert>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={onClose}>
              {t('rules.dryRun.closeButton')}
            </Button>
            <Button variant="contained" color="primary" onClick={handleRetry}>
              {t('rules.dryRun.timedOutRetryButton')}
            </Button>
          </Box>
        </Box>
      )}

      {result && (
        <Box>
          {result.status === 'failed' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {result.failureReason ?? t('general.error.somethingWentWrong')}
            </Alert>
          )}

          {isMatching && matchingMetrics && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, rowGap: 0.5, mb: 2 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
              >
                {matchingMetrics.modelId ?? '–'}
                {matchingMetrics.modelIdSource && (
                  <Label
                    text={t(
                      `rules.matching.dryRun.metrics.modelIdSource.${matchingMetrics.modelIdSource}`,
                      matchingMetrics.modelIdSource,
                    )}
                    color={matchingMetrics.modelIdSource === MatchingModelIdSource.override ? 'warning' : 'grey'}
                    sx={{ lineHeight: 'normal' }}
                  />
                )}
              </Typography>

              {!!matchingMetricsSegments.length && (
                <Typography variant="body2" color="text.secondary">
                  {matchingMetricsSegments.join(' · ')}
                </Typography>
              )}
            </Box>
          )}

          {!isColorCoding &&
            watchlistCases.map(caseResult => {
              const skips = unevaluableByCaseId.get(caseResult.caseId) ?? [];

              return (
                <Box key={caseResult.caseId} sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'grey.300' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 600 }}>
                      {caseResult.label}
                    </Typography>
                    {caseResult.status === 'failed' ? (
                      <Label
                        text={caseResult.failure?.reason ?? t('general.error.somethingWentWrong')}
                        color="error"
                        sx={{ lineHeight: 'normal' }}
                      />
                    ) : (
                      <Label
                        text={
                          caseResult.counts?.total
                            ? t('rules.dryRun.findingsCount', { count: caseResult.counts.total })
                            : t('rules.dryRun.notFired')
                        }
                        color={caseResult.counts?.total ? 'warning' : 'grey'}
                        sx={{ lineHeight: 'normal' }}
                      />
                    )}
                  </Box>

                  {(caseResult.findings ?? []).map(finding => (
                    <Box key={finding.ruleId} sx={{ mt: 1, pl: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Label
                          text={t(`rules.severity.option.${finding.severity}`, finding.severity)}
                          color={SEVERITY_LABEL_COLOR[finding.severity] ?? 'grey'}
                          sx={{ lineHeight: 'normal' }}
                        />
                        <Typography variant="body1" sx={{ flexGrow: 1 }}>
                          {finding.title} ({finding.ruleId})
                        </Typography>
                      </Box>
                      {/* The engine's own justification, shown per fire under the banner. */}
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                        {finding.justification}
                      </Typography>
                    </Box>
                  ))}

                  {/* Only the rules that could NOT be evaluated — the ordinary no-match
                (`notApplicable`) is most of the catalogue on every run and is left out. */}
                  {skips.length > 0 && (
                    <Box sx={{ mt: 1, pl: 2 }}>
                      <Button size="small" color="secondary" onClick={() => toggleCase(caseResult.caseId)}>
                        {t('rules.dryRun.notEvaluated', { count: skips.length })}
                      </Button>

                      <Collapse in={expandedCaseIds.includes(caseResult.caseId)}>
                        {skips.map(skip => (
                          <Typography key={skip.ruleId} variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            <Box component="span" sx={{ fontFamily: 'monospace' }}>
                              {skip.ruleId}
                            </Box>
                            {` — ${t(`rules.skipReason.${skip.reason}`)}`}
                            {skip.detail && (
                              <Box component="span" sx={{ fontFamily: 'monospace', ml: 0.5 }}>
                                {skip.detail}
                              </Box>
                            )}
                          </Typography>
                        ))}
                      </Collapse>
                    </Box>
                  )}
                </Box>
              );
            })}

          {isColorCoding &&
            colorCases.map(caseResult => {
              const skips = unevaluableByCaseId.get(caseResult.caseId) ?? [];

              return (
                <Box key={caseResult.caseId} sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'grey.300' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 600 }}>
                      {caseResult.label}
                    </Typography>
                    {caseResult.status === 'failed' ? (
                      <Label
                        text={caseResult.failure?.reason ?? t('general.error.somethingWentWrong')}
                        color="error"
                        sx={{ lineHeight: 'normal' }}
                      />
                    ) : (
                      <>
                        <Label
                          text={t(`rules.colorCoding.color.option.${caseResult.color}`, caseResult.color ?? '')}
                          color="grey"
                          sx={{ lineHeight: 'normal' }}
                        />
                        <Label
                          text={t('rules.colorCoding.dryRun.scoreLabel', { score: caseResult.score })}
                          color="info"
                          sx={{ lineHeight: 'normal' }}
                        />
                      </>
                    )}
                  </Box>

                  {(caseResult.breakdown ?? []).map((row, index) => (
                    <Box key={`${caseResult.caseId}-${row.ruleId ?? index}`} sx={{ mt: 1, pl: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body1" sx={{ flexGrow: 1 }}>
                          {row.ruleText ?? row.ruleId}
                        </Typography>
                        {/* A gate row carries no `points` (it only sets/caps the colour) — no chip at all
                        rather than a bare "pts" (design pass #6). */}
                        {typeof row.points === 'number' && (
                          <Label
                            text={t('rules.colorCoding.dryRun.pointsLabel', { count: row.points })}
                            color={row.points < 0 ? 'error' : 'grey'}
                            sx={{ lineHeight: 'normal' }}
                          />
                        )}
                      </Box>
                      {(row.evidence ?? []).map(evidence => (
                        <Typography key={evidence.path} variant="body2" color="text.secondary">
                          {evidence.label ?? evidence.path}: {String(evidence.answer ?? '')}
                        </Typography>
                      ))}
                    </Box>
                  ))}

                  {caseResult.justification && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, pl: 2, whiteSpace: 'pre-wrap' }}>
                      {caseResult.justification}
                    </Typography>
                  )}

                  {skips.length > 0 && (
                    <Box sx={{ mt: 1, pl: 2 }}>
                      <Button size="small" color="secondary" onClick={() => toggleCase(caseResult.caseId)}>
                        {t('rules.dryRun.notEvaluated', { count: skips.length })}
                      </Button>

                      <Collapse in={expandedCaseIds.includes(caseResult.caseId)}>
                        {skips.map(skip => (
                          <Typography key={skip.ruleId} variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            <Box component="span" sx={{ fontFamily: 'monospace' }}>
                              {skip.ruleId}
                            </Box>
                            {` — ${t(`rules.colorCoding.skipReason.${skip.reason}`)}`}
                            {skip.detail && (
                              <Box component="span" sx={{ fontFamily: 'monospace', ml: 0.5 }}>
                                {skip.detail}
                              </Box>
                            )}
                          </Typography>
                        ))}
                      </Collapse>
                    </Box>
                  )}
                </Box>
              );
            })}

          {isMatching &&
            matchingCases.map(caseResult => {
              const skips = matchingSkipsByCaseId.get(caseResult.caseId) ?? [];

              return (
                <Box key={caseResult.caseId} sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'grey.300' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 600 }}>
                      {caseResult.label}
                    </Typography>

                    {caseResult.status === MatchingDryRunCaseStatus.failed ? (
                      <Label
                        text={caseResult.failure?.reason ?? t('general.error.somethingWentWrong')}
                        color="error"
                        sx={{ lineHeight: 'normal' }}
                      />
                    ) : (
                      <>
                        {/* Colour-neutral — matching has no red/green semantics of its own (GL-3: a
                        blocking grade never forces the score to 0, the banner does the work). */}
                        <Label
                          text={t('rules.matching.dryRun.scoreLabel', { score: caseResult.score ?? 0 })}
                          color="grey"
                          sx={{ lineHeight: 'normal' }}
                        />

                        {caseResult.status === MatchingDryRunCaseStatus.completed && !caseResult.excluded && (
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            onClick={() => setJustificationCaseId(caseResult.caseId)}
                          >
                            {t('matching.justification.cta')}
                          </Button>
                        )}

                        {caseResult.excluded && (
                          <Label
                            text={t('rules.matching.dryRun.excludedLabel', { name: caseResult.excluded.name })}
                            color="error"
                            sx={{ lineHeight: 'normal' }}
                          />
                        )}

                        {(caseResult.tokensIn != null ||
                          caseResult.tokensOut != null ||
                          caseResult.durationMs != null) && (
                          <Typography variant="body2" color="text.secondary">
                            {t('rules.matching.dryRun.metrics.caseLabel', {
                              tokensIn: formatTokenCount(caseResult.tokensIn),
                              tokensOut: formatTokenCount(caseResult.tokensOut),
                              seconds: caseResult.durationMs != null ? Math.round(caseResult.durationMs / 1000) : '–',
                            })}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>

                  {!!caseResult.hardFails?.length && (
                    <Box sx={{ mt: 1, pl: 2 }}>
                      {caseResult.hardFails.map(hardFail => (
                        <Typography key={hardFail} variant="body2" color="error" sx={{ mt: 0.5 }}>
                          {hardFail}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {!!caseResult.breakdown?.length && (
                    <Box sx={{ mt: 1, pl: 2 }}>
                      {caseResult.breakdown.map(row => (
                        <Box key={row.ruleId} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.5 }}>
                          <Typography variant="body1" sx={{ flexGrow: 1 }}>
                            {row.name}{' '}
                            <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                              ({row.ruleId})
                            </Box>
                          </Typography>
                          <Label
                            text={t(matchingGradeLabelKey(row.grade), row.grade)}
                            color="info"
                            sx={{ lineHeight: 'normal' }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120, textAlign: 'right' }}>
                            {t('rules.matching.dryRun.breakdownPointsLabel', {
                              points: row.pointsEarned,
                              count: row.weight,
                            })}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {(!!caseResult.pros?.length || !!caseResult.cons?.length) && (
                    <Box sx={{ mt: 1, pl: 2 }}>
                      {caseResult.pros?.map(pro => (
                        <Typography key={pro} variant="body2" color="success" sx={{ mt: 0.5 }}>
                          + {pro}
                        </Typography>
                      ))}
                      {caseResult.cons?.map(con => (
                        <Typography key={con} variant="body2" color="error" sx={{ mt: 0.5 }}>
                          − {con}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {caseResult.summary && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, pl: 2, whiteSpace: 'pre-wrap' }}>
                      {caseResult.summary}
                    </Typography>
                  )}

                  {skips.length > 0 && (
                    <Box sx={{ mt: 1, pl: 2 }}>
                      <Button size="small" color="secondary" onClick={() => toggleCase(caseResult.caseId)}>
                        {t('rules.dryRun.notEvaluated', { count: skips.length })}
                      </Button>

                      <Collapse in={expandedCaseIds.includes(caseResult.caseId)}>
                        {skips.map(skip => (
                          <Typography key={skip.ruleId} variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {/* `name` is the matching family's own addition to the shared DTO — the
                            watchlist/colour writers never populate it, so this falls back to the
                            id (the admin bench shows ids either way, unlike the results UI). */}
                            {skip.name ?? (
                              <Box component="span" sx={{ fontFamily: 'monospace' }}>
                                {skip.ruleId}
                              </Box>
                            )}
                            {` — ${t(`rules.matching.skipReason.${skip.reason}`)}`}
                            {skip.detail && (
                              <Box component="span" sx={{ fontFamily: 'monospace', ml: 0.5 }}>
                                {skip.detail}
                              </Box>
                            )}
                          </Typography>
                        ))}
                      </Collapse>
                    </Box>
                  )}
                </Box>
              );
            })}

          {isMatching && (
            <ScoreJustificationModal
              open={!!justificationCase}
              onClose={() => setJustificationCaseId(null)}
              view={justificationView}
            />
          )}

          {/* Colour and matching dry runs carry no model-authored suggestions call (D19 is the
          watchlist's) — neither result shape has such a field, so the section is omitted entirely
          rather than rendering a permanent "no suggestions" placeholder. */}
          {!isColorCoding && !isMatching && (
            <>
              <Divider sx={{ my: 2 }} />

              <Typography variant="h4" sx={{ mb: 1 }}>
                {t('rules.dryRun.suggestionsTitle')}
              </Typography>

              {/* Fixed, i18n'd disclaimer, independent of model output. Rendered whenever a
              non-failed result exists, even if `suggestions` is absent. */}
              <Alert severity="info" inner sx={{ mb: 1 }}>
                {t('rules.dryRun.suggestionsDisclaimer')}
              </Alert>

              {result.suggestions ? (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {result.suggestions}
                </Typography>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  {t('rules.dryRun.suggestionsUnavailable')}
                </Typography>
              )}
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" color="primary" onClick={onClose}>
              {t('rules.dryRun.closeButton')}
            </Button>
          </Box>
        </Box>
      )}
    </Modal>
  );
};
